import React, { useState, useEffect } from 'react';
import { Card } from '../components/Card';
import { BarChart3, Clock, Activity, MessageSquare, Users, Building2, CreditCard, TrendingUp, Printer, Monitor, Percent, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiService, authFetch } from '../services/api';

// Kurumsal renkler: PANTONE 2728 C (#0047BB) + 2925 C (#009CDE)
type StatTone = 'navy' | 'sky' | 'blend' | 'navySoft' | 'skySoft' | 'frost';

function StatCard({
  label,
  value,
  icon: Icon,
  tone,
}: {
  label: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
  tone: StatTone;
}) {
  const shell: Record<StatTone, string> = {
    navy: 'from-[#0047BB] via-[#1a5fd4] to-[#003894] text-white shadow-[0_10px_28px_-8px_rgba(0,71,187,0.5)]',
    sky: 'from-[#009CDE] via-[#33b5e8] to-[#0077b3] text-white shadow-[0_10px_28px_-8px_rgba(0,156,222,0.45)]',
    blend: 'from-[#0047BB] via-[#0070c9] to-[#009CDE] text-white shadow-[0_10px_28px_-8px_rgba(0,71,187,0.4)]',
    navySoft: 'from-[#6b8fd4] via-[#5a7fc8] to-[#4a6fba] text-white border border-[#0047BB]/25 shadow-[0_8px_22px_-6px_rgba(0,71,187,0.35)]',
    skySoft: 'from-[#4bb4e4] via-[#2aa3d8] to-[#1490c8] text-white border border-[#009CDE]/30 shadow-[0_8px_22px_-6px_rgba(0,156,222,0.35)]',
    frost: 'from-[#7a98d4] via-[#6a8ac8] to-[#5c7cbc] text-white border border-[#0047BB]/20 shadow-[0_8px_22px_-6px_rgba(0,71,187,0.3)]',
  };

  const iconWrap: Record<StatTone, string> = {
    navy: 'bg-white/20 text-white ring-1 ring-white/25',
    sky: 'bg-white/20 text-white ring-1 ring-white/25',
    blend: 'bg-white/20 text-white ring-1 ring-white/25',
    navySoft: 'bg-white/20 text-white ring-1 ring-white/25',
    skySoft: 'bg-white/20 text-white ring-1 ring-white/25',
    frost: 'bg-white/20 text-white ring-1 ring-white/25',
  };

  return (
    <div
      className={`relative overflow-hidden rounded-xl bg-gradient-to-br p-3.5 transition-all duration-200 hover:-translate-y-0.5 hover:brightness-[1.03] ${shell[tone]}`}
    >
      <div className="pointer-events-none absolute -right-3 -top-3 h-14 w-14 rounded-full bg-white/10" />
      <div className="pointer-events-none absolute -bottom-5 -left-4 h-16 w-16 rounded-full bg-white/5" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-white/30" />
      <div className="relative flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] font-medium text-white/85">{label}</p>
          <p className="mt-1 text-xl font-bold tracking-tight text-white">{value}</p>
        </div>
        <div
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${iconWrap[tone]}`}
        >
          <Icon className="h-[18px] w-[18px]" />
        </div>
      </div>
    </div>
  );
}

function SectionTitle({
  title,
  subtitle,
  icon: Icon,
  accent = 'navy',
}: {
  title: string;
  subtitle: string;
  icon: React.ComponentType<{ className?: string }>;
  accent?: 'navy' | 'sky';
}) {
  return (
    <div className="mb-2.5 flex items-center gap-2.5">
      <div
        className={`flex h-9 w-9 items-center justify-center rounded-xl shadow-md ${
          accent === 'navy'
            ? 'bg-gradient-to-br from-[#0047BB] to-[#003894] shadow-[#0047BB]/30'
            : 'bg-gradient-to-br from-[#009CDE] to-[#0077b3] shadow-[#009CDE]/30'
        }`}
      >
        <Icon className="h-4 w-4 text-white" />
      </div>
      <div>
        <h2 className="text-base font-bold text-gray-900">{title}</h2>
        <p className="text-xs text-slate-500">{subtitle}</p>
      </div>
    </div>
  );
}

export function Dashboard() {
  const navigate = useNavigate();
  
  // İstatistik state'leri
  const [controlItems, setControlItems] = useState<any[]>([]);
  const [messageStats, setMessageStats] = useState({
    totalMessages: 0,
    pulledMessages: 0,
    successRate: 0,
    returnRate: 0
  });
  const [bagkartStats, setBagkartStats] = useState({
    totalCardholders: 0,
    totalBusinesses: 0,
    totalSpending: 0,
    totalDiscount: 0
  });
  const [bagkartDailyStats, setBagkartDailyStats] = useState({
    dailyPrinted: 0,
    pendingPrint: 0,
    handDelivered: 0,
    webApplications: 0
  });
  const [bagtvStats, setBagtvStats] = useState({
    totalTV: 0,
    activeFacilities: 0,
    totalControls: 0,
    controlledFacilities: 0
  });

  useEffect(() => {
    fetchAllStats();
  }, []);

  const fetchAllStats = async () => {
    try {
      // Tüm API çağrılarını paralel yap (çok daha hızlı!)
      const [
        items,
        messages,
        allTotalsResponse,
        businessInfoResponse,
        historyResponse,
        bagtvFacilities,
        bagtvControls
      ] = await Promise.all([
        apiService.getControlItems().catch(() => []),
        apiService.getMessages().catch(() => []),
        authFetch('/api/bagkart/all-totals').catch(() => ({ json: async () => ({ success: false }) })),
        authFetch('/api/bagkart-business-info').catch(() => ({ json: async () => ({ stats: {} }) })),
        authFetch('/api/bagkart/history?limit=1').catch(() => ({ json: async () => ({ success: false, data: [] }) })),
        apiService.getBagTVFacilities().catch(() => []),
        apiService.getBagTVControls().catch(() => [])
      ]);

      // İş Programı verilerini ayarla
      setControlItems(items || []);

      // Mesaj istatistiklerini hesapla ve ayarla
      try {
        const totalMessages = messages?.length || 0;
        const totalCount = messages?.reduce((sum: number, msg: any) => sum + (msg.totalCount || msg.total_count || 0), 0) || 0;
        const pulledCount = messages?.reduce((sum: number, msg: any) => sum + (msg.pulledCount || msg.pulled_count || 0), 0) || 0;
        const returnCount = messages?.reduce((sum: number, msg: any) => sum + (msg.returnCount || msg.return_count || 0), 0) || 0;
        const successRate = totalCount > 0 ? Math.round((pulledCount / totalCount) * 100) : 0;
        const returnRate = pulledCount > 0 ? Math.round((returnCount / pulledCount) * 100) : 0;
        
        setMessageStats({
          totalMessages,
          pulledMessages: pulledCount,
          successRate,
          returnRate
        });
      } catch (error) {
        console.error('Mesaj istatistikleri işlenirken hata:', error);
      }

      // BağKart istatistiklerini ayarla - modülle aynı veri kaynağı (bagkart-business-info)
      try {
        const [allTotalsResult, businessInfoResult, historyResult] = await Promise.all([
          allTotalsResponse.json(),
          businessInfoResponse.json(),
          historyResponse.json()
        ]);
        
        // Ana sayfa modüldeki (BağKart Üye İşyeri Bilgileri) sayıları gösterir
        const stats = businessInfoResult.stats || {};
        setBagkartStats({
          totalCardholders: stats.totalCardholders ?? 0,
          totalBusinesses: stats.totalBusinesses ?? 0,
          totalSpending: stats.totalSpending ?? 0,
          totalDiscount: stats.totalDiscount ?? 0
        });
        
        // BağKart Günlük İstatistikleri - sadece son kayıt
        if (historyResult.success && historyResult.data && historyResult.data.length > 0) {
          const latestRecord = historyResult.data[0];
          
          setBagkartDailyStats({
            dailyPrinted: latestRecord.dailyPrinted || latestRecord.daily_printed || 0,
            pendingPrint: latestRecord.pendingPrint || latestRecord.pending_print || 0,
            handDelivered: latestRecord.handDelivered || latestRecord.hand_delivered || 0,
            webApplications: latestRecord.webApplications || latestRecord.web_applications || 0
          });
        } else {
          // Veri yoksa 0 olarak ayarla
          setBagkartDailyStats({
            dailyPrinted: 0,
            pendingPrint: 0,
            handDelivered: 0,
            webApplications: 0
          });
        }
      } catch (error) {
        console.error('BağKart istatistikleri işlenirken hata:', error);
      }

      // BağTV istatistiklerini ayarla
      try {
        const formattedFacilities = bagtvFacilities?.map((f: any) => ({
          ...f,
          tvCount: f.tvCount || f.tv_count || 0
        })) || [];
        
        const totalTV = formattedFacilities.reduce((sum: number, f: any) => sum + (f.tvCount || 0), 0);
        const activeFacilities = formattedFacilities.filter((f: any) => f.status === 'active' || f.status === 'Aktif').length || 0;
        const totalControls = bagtvControls?.length || 0;
        const controlledFacilities = new Set(bagtvControls?.map((c: any) => c.facilityId || c.facility_id) || []).size;
        
        setBagtvStats({
          totalTV,
          activeFacilities,
          totalControls,
          controlledFacilities
        });
      } catch (error) {
        console.error('BağTV istatistikleri işlenirken hata:', error);
      }
    } catch (error) {
      console.error('Dashboard verileri yüklenirken hata:', error);
    }
  };

  // İş Programı istatistikleri
  const statusCounts = {
    total: controlItems.length,
    completed: controlItems.filter((item: any) => item.status === 'Tamamlandı').length,
    pending: controlItems.filter((item: any) => item.status === 'Beklemede').length,
    inProgress: controlItems.filter((item: any) => item.status === 'İşlemde').length
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('tr-TR').format(num);
  };

  const formatCurrency = (num: number) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0
    }).format(num);
  };

  return (
    <div className="h-full space-y-4 overflow-y-auto p-3">
      <h1 className="mb-1 text-xl font-bold text-gray-900">Ana Sayfa</h1>

      {/* İş Programı Özet Raporu */}
      <div>
        <SectionTitle
          title="İş Programı Özet Raporu"
          subtitle="Günlük iş programı istatistikleri"
          icon={BarChart3}
          accent="navy"
        />
        <div className="grid grid-cols-2 gap-2.5 md:grid-cols-4">
          <StatCard label="Toplam İş Kaydı" value={formatNumber(statusCounts.total)} icon={BarChart3} tone="navy" />
          <StatCard label="Tamamlanan" value={formatNumber(statusCounts.completed)} icon={CheckCircle} tone="skySoft" />
          <StatCard label="Bekleyen" value={formatNumber(statusCounts.pending)} icon={Clock} tone="frost" />
          <StatCard label="İşlemde" value={formatNumber(statusCounts.inProgress)} icon={Activity} tone="sky" />
        </div>
      </div>

      {/* Mesaj Yönetimi Özeti */}
      <div>
        <SectionTitle
          title="Mesaj Yönetimi Özeti"
          subtitle="Mesaj yönetimi ve raporlama"
          icon={MessageSquare}
          accent="sky"
        />
        <div className="grid grid-cols-2 gap-2.5 md:grid-cols-4">
          <StatCard label="Toplam Mesaj" value={formatNumber(messageStats.totalMessages)} icon={MessageSquare} tone="blend" />
          <StatCard label="Çekilen Mesaj" value={formatNumber(messageStats.pulledMessages)} icon={CheckCircle} tone="navySoft" />
          <StatCard label="Başarı Oranı" value={`${messageStats.successRate}%`} icon={Activity} tone="sky" />
          <StatCard label="Geri Dönüş Oranı" value={`${messageStats.returnRate}%`} icon={Percent} tone="frost" />
        </div>
      </div>

      {/* BağKart İstatistikleri */}
      <div>
        <SectionTitle
          title="BağKart İstatistikleri"
          subtitle="BağKart kart sistemi istatistikleri"
          icon={CreditCard}
          accent="navy"
        />
        <div className="grid grid-cols-2 gap-2.5 md:grid-cols-4">
          <StatCard label="Toplam BağKartlı" value={formatNumber(bagkartStats.totalCardholders)} icon={Users} tone="navy" />
          <StatCard label="Üye İşyeri" value={formatNumber(bagkartStats.totalBusinesses)} icon={Building2} tone="skySoft" />
          <StatCard label="Toplam Harcama" value={formatCurrency(bagkartStats.totalSpending)} icon={CreditCard} tone="blend" />
          <StatCard label="Toplam İndirim" value={formatCurrency(bagkartStats.totalDiscount)} icon={TrendingUp} tone="frost" />
        </div>
      </div>

      {/* BağKart Günlük İstatistikleri */}
      <div>
        <SectionTitle
          title="BağKart Günlük İstatistikleri"
          subtitle="Günlük basım ve teslim istatistikleri"
          icon={Printer}
          accent="sky"
        />
        <div className="grid grid-cols-2 gap-2.5 md:grid-cols-4">
          <StatCard label="Günlük Basılan" value={formatNumber(bagkartDailyStats.dailyPrinted)} icon={Printer} tone="sky" />
          <StatCard label="Basım Bekleyen" value={formatNumber(bagkartDailyStats.pendingPrint)} icon={Clock} tone="navySoft" />
          <StatCard label="Elden Teslim" value={formatNumber(bagkartDailyStats.handDelivered)} icon={Users} tone="navy" />
          <StatCard label="Web Başvuru" value={formatNumber(bagkartDailyStats.webApplications)} icon={Monitor} tone="skySoft" />
        </div>
      </div>

      {/* BağTV İstatistikleri */}
      <div>
        <SectionTitle
          title="BağTV İstatistikleri"
          subtitle="Televizyon ve tesis yönetimi"
          icon={Monitor}
          accent="navy"
        />
        <div className="grid grid-cols-2 gap-2.5 md:grid-cols-4">
          <StatCard label="Toplam TV" value={formatNumber(bagtvStats.totalTV)} icon={Monitor} tone="blend" />
          <StatCard label="Aktif Tesis" value={formatNumber(bagtvStats.activeFacilities)} icon={Building2} tone="frost" />
          <StatCard label="Tüm Kontrol" value={formatNumber(bagtvStats.totalControls)} icon={CheckCircle} tone="sky" />
          <StatCard label="Kontrol Edilen Tesis" value={formatNumber(bagtvStats.controlledFacilities)} icon={Percent} tone="navySoft" />
        </div>
      </div>

      {/* Hızlı Erişim */}
      <Card className="border-[#0047BB]/10 bg-gradient-to-br from-white to-[#f0f7fc] p-3 shadow-md">
        <h2 className="mb-2 text-sm font-semibold text-[#0047BB]">Hızlı Erişim</h2>
        <div className="grid grid-cols-4 gap-2 md:grid-cols-5 lg:grid-cols-6">
          {[
            { path: '/daily-checks', label: 'Günlük İş Programı', emoji: '📅', tone: 'navy' },
            { path: '/facilities', label: 'Tesisler', emoji: '🏢', tone: 'sky' },
            { path: '/bagkart', label: 'BagKart Yönetimi', emoji: '💳', tone: 'navy' },
            { path: '/bagtv', label: 'BağTV Yönetim', emoji: '📺', tone: 'sky' },
            { path: '/messages', label: 'Mesaj Yönetimi', emoji: '💬', tone: 'navy' },
            { path: '/approvals', label: 'Onay Yönetimi', emoji: '✅', tone: 'sky' },
            { path: '/reports', label: 'Raporlar', emoji: '📊', tone: 'navy' },
            { path: '/user-management', label: 'Kullanıcılar', emoji: '👥', tone: 'sky' },
            { path: '/settings', label: 'Ayarlar', emoji: '⚙️', tone: 'navy' },
          ].map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`rounded-xl p-2 text-center transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${
                item.tone === 'navy'
                  ? 'bg-[#0047BB]/8 text-[#0047BB] hover:bg-[#0047BB]/14'
                  : 'bg-[#009CDE]/10 text-[#0077b3] hover:bg-[#009CDE]/18'
              }`}
            >
              <div className="mb-0.5 text-base">{item.emoji}</div>
              <div className="text-[10px] font-medium leading-tight">{item.label}</div>
            </button>
          ))}
        </div>
      </Card>
    </div>
  );
} 